import React from "react";
import { useOptimizedUpcomingProcurements } from "@/hooks/use-optimized-upcoming-procurements";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, CalendarCheck, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const WarehouseUpcomingList: React.FC = () => {
  const { 
    upcomingProcurements, 
    isLoading, 
    currentPage, 
    totalPages, 
    goToPage,
    hasNextPage,
    hasPreviousPage
  } = useOptimizedUpcomingProcurements();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
        <span>Loading upcoming procurements...</span>
      </div>
    );
  }

  if (!upcomingProcurements || upcomingProcurements.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-6 text-muted-foreground">
        <CalendarCheck className="w-8 h-8 mb-2" />
        No upcoming procurements found.
      </div>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Expected Date</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Quantity</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {upcomingProcurements.map((p) => (
              <TableRow key={p.id}>
                <TableCell>
                  {p.expected_delivery_date
                    ? new Date(p.expected_delivery_date).toLocaleDateString()
                    : "N/A"}
                </TableCell>
                <TableCell>{p.product_name || "—"}</TableCell>
                <TableCell>
                  {typeof p.total_quantity === "number"
                    ? `${p.total_quantity} kg`
                    : "—"}
                </TableCell>
                <TableCell>
                  <span className="capitalize">{p.status?.replace(/_/g, ' ')}</span>
                </TableCell>
                <TableCell>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/procurements/${p.id}`)}
                  >
                    <Eye className="mr-1 h-4 w-4" />
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <span className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage - 1)}
                disabled={!hasPreviousPage}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage + 1)}
                disabled={!hasNextPage}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WarehouseUpcomingList;
